import React, { useRef, useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryItemByAisleByStatus } from "../../../../graphql/queries";
import { updateInventoryItemMini } from "../../../../graphql/inventory/custom-mutations";
import {
  createAisle,
  createLocation,
  updateAisle,
} from "../../../../graphql/mutations";
import { sanitizeInventoryItem } from "../../../../graphql/inventory/inventoryCRUD";

import {
  Backdrop,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import { Confirm, useNotify } from "react-admin";
import { AddIcon } from "../../../../components/common/iconography";
import SwitchOnOff from "../../../../components/common/AntSwitch";
import { logError } from "../../../../utils/appUtils";
import {
  AreaType,
  InventoryStatus,
  LocationLoadType,
  LocationType,
  SORTKEY_BUFFER,
  VALIDATION_MSG,
} from "../../../../utils/constants";
import {
  getAisleBadgeContent,
  getAisleFullName,
  getLocationFullName,
  getSelectedLoadTypes,
  isBulkArea,
  isTrack,
} from "../../../../utils/locationUtils";
import {
  deepRedirect,
  handleNumberFieldKeyDown,
} from "../../../../utils/utils";
import { doUpdateAisle, handleDeleteAisle } from "./aisleUtils";
import { TypeCapacity } from "./TypeCapacity";
import { UnstructuredAisleDialog } from "./UnstructuredAisleDialog";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    paddingLeft: theme.spacing(2),
  },
  item: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: theme.spacing(0.5),
  },
  location: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: grey[400],
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  locationTitle: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
  },
  locationInput: {
    marginRight: theme.spacing(2),
    minWidth: "13rem",
  },
  aisleInput: {
    marginRight: theme.spacing(2),
    maxWidth: "13rem",
  },
  topDownSwitch: {
    marginLeft: theme.spacing(2),
  },
  locationEditInput: {
    marginBottom: theme.spacing(2),
  },
  bulkCapacityditInput: {
    marginBottom: theme.spacing(2),
  },
  uomEditInput: {
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    minWidth: "12rem",
  },
  actionArea: {
    marginTop: theme.spacing(0.25),
  },
  avatar: {
    "&:hover": {
      cursor: "grab",
    },
    fontWeight: "bold",
    color: "#ffffff",
    borderRadius: 8,
    display: "flex",
    justifyContent: "space-between",
    minWidth: "6rem",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  capacityInput: {
    margin: theme.spacing(1),
    minWidth: "8rem",
  },
  typeCapacityContainer: {
    margin: theme.spacing(2),
  },
  locationInventoryType: {
    marginLeft: theme.spacing(1),
  },
  tagPooverContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
  },
  tagContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    flexWrap: "wrap",
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: grey[400],
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
  },
  tagsTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tagItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: theme.spacing(0.5),
  },
  tagsButton: {
    marginRight: theme.spacing(1),
  },
  addTagButton: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.2),
  },
  tagActionsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
}));

const MAX_LEVELS = 5;
const MAX_BAYS = 50;
const MAX_LOCATIONS_PER_LEVEL = 5;

const defaultLocationLoad = {
  other: { selected: true, capacity: 0 },
  pallet: { selected: false, capacity: 100 },
  box: { selected: false, capacity: 100 },
  container: { selected: false, capacity: 100 },
  superSack: { selected: false, capacity: 100 },
  dryBulk: { selected: false, capacity: 0 },
  liquidBulk: { selected: false, capacity: 0 },
};

const Aisles = ({ record, areas, aisles, getData, ...props }) => {
  const notify = useNotify();
  const aisleRef = useRef();
  const [loading, setLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState("");
  const [unstructuredDialogOpen, setUnstructuredDialogOpen] = useState(false);
  const [locationLoad, setLocationLoad] = useState(defaultLocationLoad);
  // const [tags, setTags] = useState([{name: 'A'}, {name: 'B'}, {name: 'C'}, {name: 'D'}]);

  const [aisleName, setAisleName] = useState();
  const [bays, setBays] = useState(1);
  const [locationsPerBayBulkError, setLocationsPerBayBulkError] =
    useState(false);
  const [levels, setLevels] = useState(1);
  const [locationsPerBay, setLocationsPerBay] = useState(1);
  const [isTopDown, setIsTopDown] = useState(true);

  const [selectedAisle, setSelectedAisle] = useState();
  const [saving, setSaving] = useState(false);
  const [aisleErrorMsg, setAisleErrorMsg] = useState();
  const classes = useStyles();
  const levelsList = Array.from({ length: MAX_LEVELS }, (_, i) => i + 1);
  const baysList = Array.from({ length: MAX_BAYS }, (_, i) => i + 1);
  const capacityList = [10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100];
  const LocationsPerBayList = Array.from(
    { length: MAX_LOCATIONS_PER_LEVEL },
    (_, i) => i + 1
  );
  const [isMobileBulk, setIsMobileBulk] = useState(false);

  useEffect(() => {
    debugger;
    if (areas && areas.length !== 0) {
      const previousSelectedAreaId = window.sessionStorage.getItem("areaId");
      let selectedArea;
      if (previousSelectedAreaId) {
        selectedArea = areas.find((itm) => itm.id === previousSelectedAreaId);
      }
      if (!selectedArea) selectedArea = areas[0];
      setSelectedArea(selectedArea);
      const previousSelectedZoneId = window.sessionStorage.getItem("zoneId");
      let selectedZoneId;
      if (previousSelectedZoneId) {
        if (
          selectedArea.zones.items.find(
            (itm) => itm.id === previousSelectedZoneId
          )
        ) {
          selectedZoneId = previousSelectedZoneId;
          setSelectedZoneId(selectedZoneId);
        }
      }
      if (!selectedZoneId) {
        if (selectedArea.zones.items.length === 1)
          setSelectedZoneId(selectedArea.zones.items[0].id);
        else setSelectedZoneId("");
      }
    }
  }, [areas]);

  const handleClickOpen = (aisle) => {
    debugger;
    setSelectedAisle({ ...aisle });
    if (aisle.type === LocationType.UNSTRUCTURED) {
      setUnstructuredDialogOpen(true);
    } else {
      deepRedirect(props.history, `/aisles/${aisle.id}`);
    }
  };

  const handleClose = (evt) => {
    evt.preventDefault();
    setSelectedAisle(null);
    setUnstructuredDialogOpen(false);
  };

  const setAisleZone = (zoneId) => {
    debugger;
    selectedAisle.zoneId = zoneId;
    const area = areas.find((itm) => itm.id === selectedAisle.areaId);
    selectedAisle.zone = area.zones.items.find((itm) => itm.id === zoneId);
    setSelectedAisle({ ...selectedAisle });
  };

  const handleAreaChange = (area) => {
    debugger;
    resetForm();
    window.sessionStorage.setItem("areaId", area.id);
    setSelectedArea(area);
    setBays(1);
    setLevels(1);
    if (area.zones.items.length === 1) {
      const zone = area.zones.items[0];
      setSelectedZoneId(zone.id);
    } else {
      setSelectedZoneId("");
    }
    setAisleName("");
  };

  const resetForm = () => {
    setAisleErrorMsg("");
    if (selectedArea.areaType === AreaType.BULK) {
      setBays(1);
      setLevels(1);
      setLocationsPerBayBulkError(false);
      setLocationLoad(defaultLocationLoad);
    }
    setLocationsPerBay(1);
  };

  const handleZoneChange = (zoneId) => {
    debugger;
    setAisleErrorMsg("");
    const selectedZone = selectedArea.zones.items.find(
      (itm) => itm.id === zoneId
    );
    setSelectedZoneId(selectedZone.id);
    window.sessionStorage.setItem("areaId", selectedArea.id);
    window.sessionStorage.setItem("zoneId", zoneId);
  };

  const setNewAisleName = (evt) => {
    debugger;
    if (evt.target.value)
      selectedAisle.name = evt.target.value.trim().toUpperCase();
    else selectedAisle.name = null;

    setSelectedAisle({ ...selectedAisle });
  };

  const setTrackLength = (evt) => {
    if (!selectedAisle) return;
    const value = evt?.target?.value;
    const parsedValue =
      value === "" || value == null ? null : Number.parseFloat(value);
    if (Number.isNaN(parsedValue)) return;
    selectedAisle.length = parsedValue;
    setSelectedAisle({ ...selectedAisle });
  };

  const isDuplicate = (toAisle) => {
    if (!toAisle) return false;
    const duplicateFlag = aisles.some(
      (itm) =>
        toAisle.id !== itm.id &&
        toAisle.name === itm.name &&
        toAisle.zone.id === itm.zoneId
    );

    return duplicateFlag;
  };

  const processInventoriesUpdate = async (selectedAisle) => {
    // handle unstructured AISLE update
    const responseList = [];
    debugger;

    const statusQueries = [
      InventoryStatus.UNLOADED,
      InventoryStatus.REVIEW,
      InventoryStatus.STORED,
    ];

    for (const queryStatus of statusQueries) {
      let nextToken = true;
      const searchQuery = {
        aisleId: selectedAisle.id,
        status: { beginsWith: queryStatus },
        limit: 1000,
      };

      while (nextToken) {
        const input =
          nextToken === true ? searchQuery : { nextToken, ...searchQuery };
        let results = await API.graphql(
          graphqlOperation(inventoryItemByAisleByStatus, input)
        );
        const inventoryItems =
          results.data.inventoryItemByAisleByStatus.items.filter(
            (item) => item.locationId
          );
        nextToken = results.data.inventoryItemByAisleByStatus.nextToken;

        const responses = await Promise.allSettled(
          inventoryItems.map((inventoryItem) => {
            return doUpdateInventoryItem(inventoryItem, selectedAisle);
          })
        );
        responseList.push(...responses);
      }
    }

    const resError = responseList.find((item) => item.status === "rejected");
    if (resError) {
      logError(
        "reloadYard:Aisles::processInventoriesUpdate:response",
        resError,
        false
      );
      throw resError;
    }
  };

  const doUpdateInventoryItem = (inventoryItem, selectedAisle) => {
    const inventoryLocation = selectedAisle.locations.items[0];
    inventoryLocation.area = selectedAisle.area;
    inventoryLocation.zone = selectedAisle.zone;
    inventoryLocation.aisle = selectedAisle;
    inventoryLocation.name = selectedAisle.name;

    const toUpdateInventoryItem = {
      ...inventoryItem,
      locationFullName: getLocationFullName(inventoryLocation),
      zoneId: selectedAisle.zoneId,
    };

    const sanitized = sanitizeInventoryItem(toUpdateInventoryItem);
    return API.graphql(
      graphqlOperation(updateInventoryItemMini, { input: sanitized })
    );
  };

  const updateAisleAndLocation = async () => {
    if (!isDuplicate(selectedAisle)) {
      try {
        debugger;
        setLoading(true);
        // TODO Remove - no longer used
        // if (selectedAisle.area.areaType === AreaType.BULK) {
        //     selectedAisle.locations.items[0].bulkCapacity = bulkCapacity;
        //     selectedAisle.locations.items[0].bulkUnits = bulkUnits;
        // }
        const updatedAisle = await doUpdateAisle(selectedAisle, true);
        await processInventoriesUpdate(updatedAisle);
        setSelectedAisle({ ...selectedAisle, ...updatedAisle });
        setUnstructuredDialogOpen(false);
        await getData();
        notify("Aisle updated succesfully.", "info");
      } catch (error) {
        logError("reloadYard:YardLayout:renameAisle", error, notify);
      } finally {
        setLoading(false);
      }
    } else {
      notify(
        `Aisle ${selectedAisle.name} already exists in:  ${selectedAisle.area.name} / ${selectedAisle.zone.name}.`,
        "warning"
      );
    }
  };

  const getAisleType = () => {
    // TODO: ask Adam
    if (selectedArea.areaType === AreaType.BULK) {
      if (isMobileBulk) {
        return LocationType.STRUCTURED;
      }
      return LocationType.BULK;
    }
    return bays === 1 && levels === 1 && locationsPerBay === 1
      ? LocationType.UNSTRUCTURED
      : LocationType.STRUCTURED;
  };

  const isSingleLocation = () => {
    return bays === 1 && levels === 1 && locationsPerBay === 1;
  };

  const isTopDownPickOrder = () => {
    if (locationLoad?.container?.selected) return true;
    if (selectedArea.areaType === AreaType.BULK) return false;
    return isTopDown;
  };

  const doCreateAisle = async (aisle) => {
    const input = {
      active: "true",
      groups: record.groups,
      reloadYardId: record.id,
      name: aisle.name,
      type: aisle.type,
      locationLoad: aisle.locationLoad || defaultLocationLoad,
      topDownPickOrder: isTopDownPickOrder(),
      bays,
      levels,
      positions: locationsPerBay,
      totalLocations: bays * levels * locationsPerBay,
      capacityUsed: 0,
      areaId: aisle.area.id,
      zoneId: aisle.zone.id,
      sortKey: aisle.sortKey,
    };
    debugger;
    const response = await API.graphql(
      graphqlOperation(createAisle, { input })
    );
    return response.data.createAisle;
  };

  const createStructuredLocations = async (aisle) => {
    debugger;
    const promiseList = [];

    for (let i = 0; i < bays; i++) {
      for (let j = 1; j <= levels; j++) {
        for (let k = 1; k <= locationsPerBay; k++) {
          const input = {
            active: "true",
            groups: record.groups,
            reloadYardId: record.id,
            level: j,
            bay: i + 1,
            position: k,
            areaId: aisle.area.id,
            zoneId: aisle.zone.id,
            aisleId: aisle.id,
            sortKey: i * levels + k,
            type: aisle.type,
            locationLoad: { ...aisle.locationLoad } || {
              ...defaultLocationLoad,
            },
            capacityUsed: 0,
            capacityAllocated: 0,
            productId: null,
          };
          if (
            aisle.locationLoad.container.selected &&
            selectedArea.areaType === AreaType.BULK
          ) {
            input.type = LocationType.BULK_MOBILE;
          }
          if (aisle.type === LocationType.UNSTRUCTURED) {
            input.name = aisle.name;
          }
          if (selectedArea.areaType === AreaType.BULK) {
            input.bulkCapacity = null;
            input.bulkUnits = "";
            if (isSingleLocation()) input.name = aisle.name;
          }
          promiseList.push(
            API.graphql(graphqlOperation(createLocation, { input }))
          );
        }
      }
    }
    const results = await Promise.all(promiseList);
    const resError = results.find((itm) => itm.status === "rejected");
    debugger;
    if (resError)
      logError(
        "reloadYard:YardLayout:Aisles:createStructuredLocations",
        resError,
        false
      );
  };

  const addAisle = async (evt) => {
    debugger;
    const submitFlag = !!evt.preventDefault;
    if (submitFlag) {
      evt.preventDefault();
    }
    if (!aisleName) {
      return;
    }
    const name = aisleName.trim().toUpperCase();
    const zone = selectedArea.zones.items.find(
      (itm) => itm.id === selectedZoneId
    );
    const type = getAisleType();
    const newAisle = {
      name,
      area: selectedArea,
      zone,
      type,
      sortKey: aisles.length + SORTKEY_BUFFER,
    };

    window.sessionStorage.setItem("zoneId", selectedZoneId);
    window.sessionStorage.setItem("areaId", selectedArea.id);

    if (isDuplicate(newAisle)) {
      setAisleErrorMsg(`Aisle ${newAisle.name} already exists.`);
      return;
    }
    if (isBulkArea(selectedArea)) {
      const isValid = isBulkDataValid();
      if (!isValid) {
        notify(VALIDATION_MSG, "warning");
        return;
      }
    }
    setAisleErrorMsg("");
    setLoading(true);
    setSaving(true);
    setAisleName("");
    setLocationsPerBayBulkError(false);

    await addStructuredAisle(newAisle);
    setSaving(false);
    aisleRef.current.focus();
    setLoading(false);
  };

  const isBulkDataValid = () => {
    if (locationsPerBay < 0 || isNaN(locationsPerBay)) {
      return false;
    }
    return true;
  };

  const addStructuredAisle = async (aisle) => {
    try {
      const newAisle = await doCreateAisle(aisle);
      await createStructuredLocations({ ...newAisle });
      await getData();
      notify(
        `Aisle ${getAislePrefix()}-${aisle.name} was succesfully created.`,
        "info"
      );
    } catch (error) {
      logError("reloadYard:YardLayout:addAisle", error, notify);
    }
  };

  const getAislePrefix = () => {
    if (!selectedZoneId) return "";
    const areaPrefix = selectedArea.prefix;
    const selectedZone = selectedArea.zones.items.find(
      (itm) => itm.id === selectedZoneId
    );
    const zonePrefix = selectedZone.prefix;
    return `${areaPrefix}${zonePrefix}`;
  };

  const handleAisleName = (evt) => {
    setAisleErrorMsg("");
    if (!evt.target.value) {
      setAisleName("");
      return;
    }
    const value = evt.target.value.trim().toUpperCase();
    value && setAisleName(value);
  };

  const confirmDeleteAisle = (aisle) => {
    debugger;
    if (!aisle) return;

    setSelectedAisle(aisle);
    if (aisle.type === LocationType.UNSTRUCTURED) {
      setConfirmContent(`Are you sure you want to delete Aisle ${aisle.name}?`);
    } else {
      setConfirmContent(
        `Deleting an Aisle will delete all of its locations, are you sure you want to delete Aisle ${aisle.name}?`
      );
    }
    setOpenDelete(true);
  };

  const processAisleDelete = async () => {
    try {
      setLoading(true);
      setOpenDelete(false);

      await handleDeleteAisle(selectedAisle, notify);

      setSelectedAisle(null);
      await getData();
    } catch (error) {
      logError("reloadYard:Aisles:processAisleDelete", error, false);
    } finally {
      setLoading(false);
    }
  };

  const doUpdateAisles = async () => {
    const results = await Promise.allSettled(
      aisles.map((aisle) => {
        const toUpdateAisle = { ...aisle };
        delete toUpdateAisle.area;
        delete toUpdateAisle.zone;
        delete toUpdateAisle.createdAt;
        delete toUpdateAisle.updatedAt;
        delete toUpdateAisle.locations;
        return API.graphql(
          graphqlOperation(updateAisle, { input: toUpdateAisle })
        );
      })
    );

    const resError = results.find((itm) => itm.status === "rejected");
    debugger;
    if (resError)
      logError("reloadYard:YardLayout:Aisles:doUpdateAisles", resError, false);
    else notify("Aisle updated succesfully.", "info");
  };

  const allowDrop = (evt) => {
    evt.preventDefault();
  };

  const drag = (evt, aisle) => {
    evt.dataTransfer.setData("data", aisle.id);
  };

  const drop = async (evt, toAisle) => {
    try {
      setLoading(true);
      debugger;
      const toIdx = aisles.findIndex((itm) => itm.id === toAisle.id);
      const fromAisleId = evt.dataTransfer.getData("data");
      const fromIdx = aisles.findIndex((itm) => itm.id === fromAisleId);
      const fromAisle = aisles[fromIdx];
      if (fromAisle.areaId !== toAisle.areaId) return;
      evt.preventDefault();
      aisles.splice(fromIdx, 1);
      aisles.splice(toIdx, 0, fromAisle);
      aisles.forEach((itm, idx) => (itm.sortKey = idx + SORTKEY_BUFFER));
      await doUpdateAisles(aisles);
      await getData();
    } catch (err) {
      logError("reloadYard:YardLayout:Aisles:drop", err, false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newLocationLoad = { ...defaultLocationLoad };
    if (isBulkArea(selectedArea)) {
      newLocationLoad[LocationLoadType.OTHER].selected = false;
    }
    setLocationLoad(newLocationLoad);
  }, [selectedArea]);

  const handleLocationLoadType = (evt) => {
    debugger;
    const name = evt.target.name;
    const newLocationLoad = { ...locationLoad };

    if (isBulkArea(selectedArea)) {
      if (name === LocationLoadType.DRY_BULK) {
        newLocationLoad[LocationLoadType.LIQUID_BULK].selected = false;
      } else if (name === LocationLoadType.LIQUID_BULK) {
        newLocationLoad[LocationLoadType.DRY_BULK].selected = false;
      }

      // Update the selected state first
      newLocationLoad[name].selected = !newLocationLoad[name].selected;

      // Then check if container is selected to update mobile bulk state
      if (name === LocationLoadType.CONTAINER) {
        setIsMobileBulk(newLocationLoad[name].selected);
      }

      setLocationLoad(newLocationLoad);
      return;
    }

    if (
      name === LocationLoadType.CONTAINER ||
      name === LocationLoadType.OTHER
    ) {
      for (let key in locationLoad) locationLoad[key].selected = false;
    } else {
      locationLoad[LocationLoadType.CONTAINER].selected = false;
      locationLoad[LocationLoadType.OTHER].selected = false;
    }
    if (name === LocationLoadType.CONTAINER) setLocationsPerBay(1);
    locationLoad[evt.target.name].selected = evt.target.checked;
    const notSelected = Object.keys(locationLoad).reduce(
      (val, k) => val || locationLoad[k].selected,
      false
    );
    if (!notSelected) locationLoad.other.selected = true;
    setLocationLoad({ ...locationLoad });
  };

  const handleTypeCapacity = (value, typeName) => {
    debugger;
    locationLoad[typeName].capacity = Number(value);
    setLocationLoad({ ...locationLoad });
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setSelectedAisle(null);
  };

  const showStructuredCapacity = () => {
    // always show for bulk locations
    if (isBulkArea(selectedArea)) return false;
    // hide for UNSTRUCTURED Aisles = single location
    if (isSingleLocation()) return false;
    return true;
  };

  const handleLocationsPerBayChange = (evt) => {
    debugger;
    if (selectedArea.areaType === AreaType.BULK) {
      setLocationsPerBayBulkError(false);
    }
    setLocationsPerBay(evt.target.value ? Number(evt.target.value) : "");
  };

  const getControl = () => {
    return (
      <>
        {!isBulkArea(selectedArea) && !isTrack(selectedArea) && (
          <>
            <Grid item xs={3}>
              <TextField
                name="bays"
                label="No. of Bays"
                size="small"
                variant="outlined"
                fullWidth
                disabled={saving || selectedArea.areaType === AreaType.BULK}
                onChange={(evt) => setBays(Number(evt.target.value))}
                select
                required
                value={bays}
              >
                {baysList.map((itm) => (
                  <MenuItem key={itm} value={itm}>
                    {itm}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                name="levels"
                label="No. of Levels"
                size="small"
                variant="outlined"
                fullWidth
                disabled={saving || selectedArea.areaType === AreaType.BULK}
                onChange={(evt) => setLevels(Number(evt.target.value))}
                select
                value={levels}
              >
                {levelsList.map((itm) => (
                  <MenuItem key={itm} value={itm}>
                    {itm}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                name="positions"
                label="Locations/Level"
                size="small"
                variant="outlined"
                fullWidth
                disabled={
                  saving || locationLoad[LocationLoadType.CONTAINER].selected
                }
                onChange={handleLocationsPerBayChange}
                select
                value={locationsPerBay}
              >
                {locationLoad[LocationLoadType.CONTAINER].selected
                  ? [
                      <MenuItem key={1} value={1}>
                        1
                      </MenuItem>,
                    ]
                  : LocationsPerBayList.map((itm) => (
                      <MenuItem key={itm} value={itm}>
                        {itm}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
          </>
        )}
        {isBulkArea(selectedArea) && (
          <Grid container item spacing={2}>
            <Grid item xs={3}>
              <TypeCapacity
                bulk
                handleTypeCapacity={handleTypeCapacity}
                locationLoad={locationLoad}
                handleLocationLoadType={handleLocationLoadType}
                capacityList={capacityList}
                getSelectedLoadTypes={getSelectedLoadTypes}
              />
            </Grid>

            {isMobileBulk ? (
              <>
                <Grid item xs={3}>
                  <TextField
                    name="bays"
                    label="No. of Bays"
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled={saving}
                    onChange={(evt) => setBays(Number(evt.target.value))}
                    select
                    required
                    value={bays}
                  >
                    {baysList.map((itm) => (
                      <MenuItem key={itm} value={itm}>
                        {itm}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    name="levels"
                    label="No. of Levels"
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled={saving}
                    onChange={(evt) => setLevels(Number(evt.target.value))}
                    select
                    value={levels}
                  >
                    {levelsList.map((itm) => (
                      <MenuItem key={itm} value={itm}>
                        {itm}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    name="positions"
                    label="Locations/Level"
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled={saving}
                    onChange={handleLocationsPerBayChange}
                    select
                    value={locationsPerBay}
                  >
                    {LocationsPerBayList.map((itm) => (
                      <MenuItem key={itm} value={itm}>
                        {itm}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            ) : (
              <Grid item xs={3}>
                <TextField
                  id="bulk-positions"
                  label="No of Locations"
                  size="small"
                  variant="outlined"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={saving}
                  required
                  value={locationsPerBay}
                  error={locationsPerBayBulkError}
                  helperText={locationsPerBayBulkError && "Required"}
                  onChange={handleLocationsPerBayChange}
                  onKeyDown={handleNumberFieldKeyDown}
                />
              </Grid>
            )}
          </Grid>
        )}

        {showStructuredCapacity() && (
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TypeCapacity
                handleTypeCapacity={handleTypeCapacity}
                locationLoad={locationLoad}
                handleLocationLoadType={handleLocationLoadType}
                capacityList={capacityList}
                getSelectedLoadTypes={getSelectedLoadTypes}
              />
            </Grid>
            <Grid item>
              {!locationLoad?.container?.selected && (
                <Box mt={1.5}>
                  <SwitchOnOff
                    leftLabel="Racks"
                    rightLabel="Floor-loaded"
                    checked={isTopDown}
                    setChecked={setIsTopDown}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const isAddAisleButtonDisabled = () => {
    const inputsEmpty = !aisleName || !selectedZoneId || !selectedArea;
    const locationLoadEmpty = Object.values(locationLoad).every(
      (load) => !load.selected
    );
    const isStructured = isBulkArea(selectedArea) || !isSingleLocation();
    return inputsEmpty || (isStructured && locationLoadEmpty);
  };

  if (!selectedArea) return <></>;
  return (
    <>
      {!unstructuredDialogOpen && (
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <>
        <form onSubmit={addAisle} className={classes.container}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                select
                id="area"
                label="Area"
                required
                value={selectedArea}
                size="small"
                fullWidth
                variant="outlined"
                onChange={(e) => handleAreaChange(e.target.value)}
              >
                {areas.map((itm) => (
                  <MenuItem key={itm.id} value={itm}>
                    {itm.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                select
                id="zone"
                label="Zone"
                required
                value={selectedZoneId}
                size="small"
                fullWidth
                variant="outlined"
                onChange={(e) => handleZoneChange(e.target.value)}
              >
                {selectedArea.zones.items.map((itm) => (
                  <MenuItem key={itm.id} value={itm.id}>
                    {itm.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                name="aisleName"
                label="Aisle"
                value={aisleName}
                required
                size="small"
                fullWidth
                variant="outlined"
                disabled={saving}
                error={aisleErrorMsg}
                helperText={aisleErrorMsg && aisleErrorMsg}
                onChange={handleAisleName}
                inputRef={aisleRef}
                inputProps={{ style: { textTransform: "uppercase" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getAislePrefix()}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addAisle}
                disabled={isAddAisleButtonDisabled()}
              >
                Add
              </Button>
            </Grid>
            {/* <TagMenu
                  tags={tags}
                  classes={classes}
              /> */}
            {getControl()}
            {/* <Box mt={aisleType === LocationType.STRUCTURED ? 2 : -1} mb={1}>
                  <FormControl component="fieldset">
                      <FormGroup>
                          <FormLabel component="legend">Location type</FormLabel>
                          <RadioGroup
                              row
                              name="locationStorageType"
                              // value={locationStorageType}
                              // onChange={handleTypeChange}
                          >
                              <FormControlLabel value="storage" control={<Radio />} label="Storage" />
                              <FormControlLabel value="shippmentIn" control={<Radio />} label="Shippment In" />
                              <FormControlLabel value="shippmentOut" control={<Radio />} label="Shippment Out" />
                              <FormControlLabel value="clearing" control={<Radio />} label="Clearing" />
                              <FormControlLabel value="other" control={<Radio />} label="Other" />
                          </RadioGroup>
                      </FormGroup>
                  </FormControl>
                </Box>  */}
            {/* </div> */}
          </Grid>
        </form>
        {areas.map((area) => (
          <div key={area.id}>
            <Typography variant="subtitle2" className={classes.locationTitle}>
              {area.name}
            </Typography>
            <div className={classes.location}>
              {aisles.filter((itm) => itm.areaId === area.id).length === 0 ? (
                loading ? (
                  <></>
                ) : (
                  <Typography color="textSecondary">
                    Enter Inventory locations (e.g. 1, 1-A, 1-B, 2-A, 2-B, 3...)
                    in a "sequential" order. <br />
                    Starting from the beginning, and walking aisle by aisle,
                    enter one location at a time. <br />
                    Hit ENTER / ADD after each location until you've added all
                    marked locations in your transload yard.
                  </Typography>
                )
              ) : (
                aisles
                  .filter((itm) => itm.areaId === area.id)
                  .map((aisle, idx) => (
                    <div key={aisle.id} className={classes.item}>
                      {aisle.type === LocationType.UNSTRUCTURED ? (
                        <Chip
                          label={getAisleFullName(aisle)}
                          draggable="true"
                          onDragStart={(evt) => drag(evt, aisle)}
                          onDragOver={(evt) => allowDrop(evt, aisle)}
                          onDrop={(evt) => drop(evt, aisle)}
                          clickable
                          onClick={() => handleClickOpen(aisle)}
                          onDelete={() => confirmDeleteAisle(aisle)}
                          className={classes.avatar}
                          style={{
                            backgroundColor: aisle.zone.color
                              ? aisle.zone.color
                              : "#90A4AE",
                          }}
                        />
                      ) : (
                        <Badge
                          badgeContent={getAisleBadgeContent(aisle)}
                          color="primary"
                        >
                          <Chip
                            label={getAisleFullName(aisle)}
                            draggable="true"
                            onDragStart={(evt) => drag(evt, aisle)}
                            onDragOver={(evt) => allowDrop(evt, aisle)}
                            onDrop={(evt) => drop(evt, aisle)}
                            clickable
                            onClick={() => handleClickOpen(aisle)}
                            onDelete={() => confirmDeleteAisle(aisle)}
                            className={classes.avatar}
                            style={{
                              backgroundColor: aisle.zone.color
                                ? aisle.zone.color
                                : "#90A4AE",
                            }}
                          />
                        </Badge>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        ))}
        {unstructuredDialogOpen && (
          <UnstructuredAisleDialog
            selectedLocation={selectedAisle}
            area={areas.find((itm) => itm.id === selectedAisle.areaId)}
            setAisleZone={setAisleZone}
            setNewLocationName={setNewAisleName}
            setTrackLength={setTrackLength}
            classes={classes}
            updateAisleAndLocation={updateAisleAndLocation}
            open={unstructuredDialogOpen}
            handleClose={handleClose}
            loading={loading}
          />
        )}
      </>
      <Confirm
        isOpen={openDelete}
        loading={loading}
        title={"Delete Confirmation"}
        content={confirmContent}
        onConfirm={processAisleDelete}
        onClose={handleDeleteCancel}
      />
    </>
  );
};

export default Aisles;
